import { useState, useEffect } from "react";
import FlowMeterChoice from "../../components/FlowMeterChoice";
import FlowMeasurementChoice from "../../components/FlowMeasurementChoice";
import { HiCalculator } from "react-icons/hi";
import BackButton from "../../components/BackButton";
import { parseISO, isBefore } from "date-fns";
import { toast } from "sonner";
import api from "../../api/manage_calculations/JetsonReq";
import Spinner from "../../components/Spinner";
import ParamsInputs from "../../components/ParamsInputs";
import TitleWithReloadButton from "../../components/TitleWithReloadButton";
import Labels from "../../components/Labels";

const StdHistorical = () => {

    const [selectedFlowMeter, setSelectedFlowMeter] = useState('');
    const [dateTimeFrom, setDateTimeFrom] = useState('');
    const [dateTimeTo, setDateTimeTo] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const initBase = {
        'Base': 'Not calculated'
    }

    const [base, setBase] = useState(initBase);

    const initdata = {
        'treshold': '',
        'cutoff_lev': '',
        'fs': '',
        'butter_order': '',
        'window_len': '',
        'min_section_len_multiplier': '',
        'eps': '',
    }
    
    const formatData = {
        'treshold': ['any', null],
        'cutoff_lev': ['1', 0],
        'fs': ['any', null],
        'butter_order': ['1', 0],
        'window_len': ['1', 0],
        'min_section_len_multiplier': ['1', 0],
        'eps': ['any', null],
    }

    const [params, setParams] = useState(initdata);

    const checkParamsEmpty = () => {
        return Object.values(params).some((value) => value === '' || value == null)
    }

    const handleDefaultParams = async () => {
        setIsLoading(true);
        try {
            const response = await api.get(
                `default-calculation-params/${selectedFlowMeter}/historical`,
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                setParams(response.data.payload);
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            console.log(err.response);
        }
        setIsLoading(false);
    }

    const handleDeleteParams = async () => {
        setIsLoading(true);
        try {
            const response = await api.delete(
                `calculation-params/${selectedFlowMeter}/historical`,
                {
                    withCredentials: true
                }
            )
            if (response.data.success) {
                setParams(initdata);
                toast.success(response.data.message)
            } else {
                toast.error(response.data.message)
            }        
        } catch (err) {
            console.log(err.response);
        }
        setIsLoading(false);
    }

    const handleSaveParams = async () => {
        if (checkParamsEmpty()) {
            toast.error("Fill in all params!");
            return
        }
        setIsLoading(true);
        try {
            const response = await api.post(
                'save-calculation-params-std',
                {
                    flow_meter_id: selectedFlowMeter,
                    params_type: "historical",
                    params: params
                },
                {
                    withCredentials: true
                }
            )
            if (response.data.success) {
                toast.success(response.data.message)
            }
        } catch (err) {
            console.log(err.response)
            toast.error("Something go wrong!\nCheck logs!")
        }
        setIsLoading(false);
    }

    const handleCalculate = async () => {
        setIsLoading(true);
        const dateTimeFromParsed = parseISO(dateTimeFrom);
        const dateTimeToParsed = parseISO(dateTimeTo);
        
        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            toast.error('Error! First datetime is not earlier!')
            return
        }

        if (checkParamsEmpty()) {
            toast.error("Fill in all params!");
            return
        }

        try {
            const response = await api.post(
                "calculate-historical-std",
                {
                    datetime_from: dateTimeFrom,
                    datetime_to: dateTimeTo,
                    flow_meter_id: selectedFlowMeter
                },
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                toast.success(response.data.message);
            }
        } catch (err) {
            console.log(err.response);
        }
        setIsLoading(false);
    }

    useEffect(() => {    
        const getCalculationParams = async () => {
            if (!selectedFlowMeter) return; 
            try {
                const response = await api.get(
                    `calculation-params/${selectedFlowMeter}/historical`,
                    {
                        withCredentials: true
                    }
                )
                if (response.data.success) {
                    setParams(response.data.payload);
                } else {
                    setParams(initdata);
                    toast.error(response.data.message);
                }
                
            } catch (err) {
                console.log(err.response);
            }
        }
        if (selectedFlowMeter !== "")
            getCalculationParams();
    }, [selectedFlowMeter])

    useEffect(() => {    
        const getBase = async () => {
            if (!selectedFlowMeter) return; 
            try {
                const response = await api.get(
                    `calculation-pattern-std/${selectedFlowMeter}`,
                    {
                        withCredentials: true
                    }
                )
                if (response.data.success) {
                    if (response.data.payload.a === 0 && response.data.payload.b === 0) {
                        setBase(initBase); 
                    } else {
                        setBase({'Base': response.data.payload.base});
                    }                       
                } else {
                    setBase(initBase);
                    toast.error(response.data.message);
                }
                
            } catch (err) {
                console.log(err.response);
            }
        }
        if (selectedFlowMeter !== "")
            getBase();
    }, [selectedFlowMeter])

    return (
        <div className="std-historical-container">
            <TitleWithReloadButton
                title="Std Historical"
            />
            <div className="std-historical-card">
                <Labels
                    title=""
                    data={base}
                />
                <FlowMeterChoice 
                    setSelectedFlowMeter={setSelectedFlowMeter}
                    disabled={isLoading}
                />
                <FlowMeasurementChoice 
                    setDateTimeFrom={setDateTimeFrom}
                    setDateTimeTo={setDateTimeTo}
                    disabled={isLoading}
                />
                {isLoading? <button className="std-historical-button-warning">
                    <Spinner size="small" />
                </button>: <button className="std-historical-button-warning" onClick={handleDefaultParams}>
                    <div>Set default parameters</div>
                </button>}
                <ParamsInputs
                    data={params}
                    setData={setParams}
                    disabled={isLoading}
                    formatData={formatData}
                />
                <div className="std-historical-buttons-div">
                    {isLoading? <button className="std-historical-button std-historical-button-delete">
                        <Spinner size="small" />
                    </button>:
                    <button className="std-historical-button std-historical-button-delete" onClick={handleDeleteParams}>
                        Delete params
                    </button>}
                    {isLoading? <button className="std-historical-button">
                        <Spinner size="small" />
                    </button>:
                    <button className="std-historical-button" onClick={handleSaveParams}>
                        Save parameters
                    </button>}
                </div>
                
                {isLoading? <button className="std-historical-button">
                    <Spinner size="small" />
                </button>:
                <button className="std-historical-button" onClick={handleCalculate}>
                    <div>Calculate <HiCalculator /></div>
                </button>}
            </div>
            <BackButton 
                navigateTo='/manage-calculations'
            />
        </div>   
    )
}

export default StdHistorical;